import { BaseCollection } from './base-collection.model';
import { Supplier } from './supplier.model';
import { BasicUser } from './user.model';

export enum SessionErrorLayer {
  TCP = 'TCP',
  SMPP = 'SMPP',
}

export enum SessionFlowStage {
  SESSION = 'session',
  CONNECTION = 'connection',
  BIND = 'bind',
}

export enum SessionStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  RECONNECTING = 'RECONNECTING',
  CLOSING = 'CLOSING',
  SHUTDOWN = 'SHUTDOWN',
}

export enum SessionErrorKey {
  TCP_CONNECT_TIMEOUT = 'TCP_CONNECT_TIMEOUT',
  TCP_CHANNEL_UNEXPECTEDLY_CLOSED = 'TCP_CHANNEL_UNEXPECTEDLY_CLOSED',
  TCP_CHANNEL_CLOSED_AFTER_REQUEST_SENT = 'TCP_CHANNEL_CLOSED_AFTER_REQUEST_SENT',
  SESSION_COLLECTOR_SHUTDOWN_REQUESTED = 'SESSION_COLLECTOR_SHUTDOWN_REQUESTED',
  SESSION_COLLECTOR_SHUTDOWN_DUPLICATE_SESSION = 'SESSION_COLLECTOR_SHUTDOWN_DUPLICATE_SESSION',
  SESSION_COLLECTOR_CLEANUP_STOPPING = 'SESSION_COLLECTOR_CLEANUP_STOPPING',
  SMPP_BIND_ALREADY_BOUND = 'SMPP_BIND_ALREADY_BOUND',
  SMPP_BIND_INVALID_CREDENTIALS = 'SMPP_BIND_INVALID_CREDENTIALS',
  SMPP_BIND_FAILED = 'SMPP_BIND_FAILED',
  SMPP_SESSION_NO_RESPONSE = 'SMPP_SESSION_NO_RESPONSE',
  SMPP_SESSION_UNBIND_REQUEST_RECEIVED = 'SMPP_SESSION_UNBIND_REQUEST_RECEIVED',
  SMPP_SESSION_UNKNOWN_THROWABLE = 'SMPP_SESSION_UNKNOWN_THROWABLE',
  SMPP_UNKNOWN_ERROR = 'SMPP_UNKNOWN_ERROR',
}

export enum SessionFilterStatus {
  ONLINE = 'ONLINE',
  DISABLED = 'DISABLED',
  TCP_CONNECT_TIMEOUT = SessionErrorKey.TCP_CONNECT_TIMEOUT,
  TCP_CHANNEL_UNEXPECTEDLY_CLOSED = SessionErrorKey.TCP_CHANNEL_UNEXPECTEDLY_CLOSED,
  TCP_CHANNEL_CLOSED_AFTER_REQUEST_SENT = SessionErrorKey.TCP_CHANNEL_CLOSED_AFTER_REQUEST_SENT,
  SESSION_COLLECTOR_SHUTDOWN_REQUESTED = SessionErrorKey.SESSION_COLLECTOR_SHUTDOWN_REQUESTED,
  SESSION_COLLECTOR_SHUTDOWN_DUPLICATE_SESSION = SessionErrorKey.SESSION_COLLECTOR_SHUTDOWN_DUPLICATE_SESSION,
  SESSION_COLLECTOR_CLEANUP_STOPPING = SessionErrorKey.SESSION_COLLECTOR_CLEANUP_STOPPING,
  SMPP_BIND_ALREADY_BOUND = SessionErrorKey.SMPP_BIND_ALREADY_BOUND,
  SMPP_BIND_INVALID_CREDENTIALS = SessionErrorKey.SMPP_BIND_INVALID_CREDENTIALS,
  SMPP_BIND_FAILED = SessionErrorKey.SMPP_BIND_FAILED,
  SMPP_SESSION_NO_RESPONSE = SessionErrorKey.SMPP_SESSION_NO_RESPONSE,
  SMPP_SESSION_UNBIND_REQUEST_RECEIVED = SessionErrorKey.SMPP_SESSION_UNBIND_REQUEST_RECEIVED,
  SMPP_SESSION_UNKNOWN_THROWABLE = SessionErrorKey.SMPP_SESSION_UNKNOWN_THROWABLE,
  SMPP_UNKNOWN_ERROR = SessionErrorKey.SMPP_UNKNOWN_ERROR,
}

export const SESSION_STATUS_SHORT_ERROR_TEXT: Record<SessionErrorKey, string> = {
  [SessionErrorKey.TCP_CONNECT_TIMEOUT]: 'Connect timeout',
  [SessionErrorKey.TCP_CHANNEL_UNEXPECTEDLY_CLOSED]: 'Channel closed',
  [SessionErrorKey.TCP_CHANNEL_CLOSED_AFTER_REQUEST_SENT]: 'Request interrupted',
  [SessionErrorKey.SESSION_COLLECTOR_SHUTDOWN_REQUESTED]: 'Shutdown requested',
  [SessionErrorKey.SESSION_COLLECTOR_SHUTDOWN_DUPLICATE_SESSION]: 'Duplicate session',
  [SessionErrorKey.SESSION_COLLECTOR_CLEANUP_STOPPING]: 'Cleanup stopping',
  [SessionErrorKey.SMPP_BIND_ALREADY_BOUND]: 'Already bound',
  [SessionErrorKey.SMPP_BIND_INVALID_CREDENTIALS]: 'Invalid credentials',
  [SessionErrorKey.SMPP_BIND_FAILED]: 'Bind failed',
  [SessionErrorKey.SMPP_SESSION_NO_RESPONSE]: 'No response',
  [SessionErrorKey.SMPP_SESSION_UNBIND_REQUEST_RECEIVED]: 'Unbind received',
  [SessionErrorKey.SMPP_SESSION_UNKNOWN_THROWABLE]: 'Internal error',
  [SessionErrorKey.SMPP_UNKNOWN_ERROR]: 'Unknown error',
};

export const SESSION_FILTER_STATUS_TEXT: Record<SessionFilterStatus, string> = {
  [SessionFilterStatus.ONLINE]: 'Online',
  [SessionFilterStatus.DISABLED]: 'Disabled',
  [SessionFilterStatus.TCP_CONNECT_TIMEOUT]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.TCP_CONNECT_TIMEOUT],
  [SessionFilterStatus.TCP_CHANNEL_UNEXPECTEDLY_CLOSED]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.TCP_CHANNEL_UNEXPECTEDLY_CLOSED],
  [SessionFilterStatus.TCP_CHANNEL_CLOSED_AFTER_REQUEST_SENT]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.TCP_CHANNEL_CLOSED_AFTER_REQUEST_SENT],
  [SessionFilterStatus.SESSION_COLLECTOR_SHUTDOWN_REQUESTED]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.SESSION_COLLECTOR_SHUTDOWN_REQUESTED],
  [SessionFilterStatus.SESSION_COLLECTOR_SHUTDOWN_DUPLICATE_SESSION]:
    SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.SESSION_COLLECTOR_SHUTDOWN_DUPLICATE_SESSION],
  [SessionFilterStatus.SESSION_COLLECTOR_CLEANUP_STOPPING]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.SESSION_COLLECTOR_CLEANUP_STOPPING],
  [SessionFilterStatus.SMPP_BIND_ALREADY_BOUND]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.SMPP_BIND_ALREADY_BOUND],
  [SessionFilterStatus.SMPP_BIND_INVALID_CREDENTIALS]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.SMPP_BIND_INVALID_CREDENTIALS],
  [SessionFilterStatus.SMPP_BIND_FAILED]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.SMPP_BIND_FAILED],
  [SessionFilterStatus.SMPP_SESSION_NO_RESPONSE]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.SMPP_SESSION_NO_RESPONSE],
  [SessionFilterStatus.SMPP_SESSION_UNBIND_REQUEST_RECEIVED]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.SMPP_SESSION_UNBIND_REQUEST_RECEIVED],
  [SessionFilterStatus.SMPP_SESSION_UNKNOWN_THROWABLE]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.SMPP_SESSION_UNKNOWN_THROWABLE],
  [SessionFilterStatus.SMPP_UNKNOWN_ERROR]: SESSION_STATUS_SHORT_ERROR_TEXT[SessionErrorKey.SMPP_UNKNOWN_ERROR],
};

export interface SessionErrorDetails {
  hostIp: string;
  statusCode: SessionStatus | null;
  errorLayer: SessionErrorLayer | null;
  flowStage: SessionFlowStage | null;
  errorKey: SessionErrorKey | null;
  shortText: string;
  commandStatusHex: string | null;
  commandStatusName: string | null;
  description: string;
  guidance: string;
  lastError: string;
  lastErrorAt: Date | null;
  lastErrorAtFormatted: string | null;
  lastErrorAtAbsoluteFormatted: string | null;
  failedTimes: number;
  isReconnecting: boolean;
  isConnecting: boolean;
  isAutoRetrying: boolean;
}

export interface Session {
  id?: number;
  userId?: number;
  dstNpi?: number;
  dstTon?: number;
  enabled?: boolean;
  online?: boolean;
  visibleAll?: boolean;
  hostIp?: string;
  hostPort?: number;
  password?: string;
  systemId?: string;
  systemType?: string;
  throughput?: number;
  supplierCount?: number;
  concatenationType?: string;
  windowSize?: number;
  windowWaitTimeout?: number;
  bindTimeout?: number;
  connectTimeout?: number;
  enableCounters?: boolean;
  interfaceVersion?: number;
  requestExpiryTimeout?: number;
  useLogPduOption?: boolean;
  windowMonitorInterval?: number;
  writeTimeout?: number;
  useSSL?: boolean;
  user?: BasicUser;
  suppliers?: Supplier[];
  alertEnabled?: boolean;
  alertEmails?: string[];
  // error details
  statusCode?: SessionStatus;
  lastError?: string;
  errorLayer?: SessionErrorLayer;
  flowStage?: SessionFlowStage;
  errorKey?: SessionErrorKey;
  commandStatus?: number;
  lastErrorAt?: string | Date;
  failedTimes?: number;
}

export interface SessionSelect extends Session {
  isSelected?: boolean;
}

export interface SessionsCollection extends BaseCollection {
  content: Session[];
}
